import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import NavBar from "../components/NavBar/NavBar";
import { DumbbellIcon, CardioIcon, RestIcon, CancelIcon, InfoIcon, CheckIcon } from "../assets/icons/icons";
import "../components/MealModal/style.css";
import "../components/DayTracker/style.css";
import "../components/WorkoutModal/style.css";
import exercisesData from "../assets/exercises.json";
import ExerciseModal from "../components/ExerciseModal/ExerciseModal";
import TrainingDaysModal from "../components/TrainingDaysModal/TrainingDaysModal";
import { getMyWorkoutPreferences, upsertWorkoutPreferences } from "../api/profile";
import LoadingAnimation from "../components/LoadingAnimation/LoadingAnimation";
import { apiFetch } from "../api/apiClient";
import workoutPlanIllustration from "../assets/workout-plan.jpg";

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const EXERCISE_IMAGE_BUCKET = process.env.REACT_APP_SUPABASE_EXERCISE_IMAGE_BUCKET || 'exercise-images';
const EXERCISE_IMAGE_URL_TTL = 60 * 60;
const normalizeName = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const equipmentTokens = new Set([
  'dumbbell', 'barbell', 'machine', 'cable', 'band', 'bands', 'body', 'bodyweight', 'kettlebell', 'lever', 'smith', 'variation', 'version',
]);
const ignoreTokens = new Set([
  'finisher', 'finishers', 'burnout', 'burnouts', 'conditioning', 'circuit', 'circuits', 'hiit', 'tabata', 'interval', 'intervals',
]);
const manualExerciseFallbacks = [
  {
    match: (name) => normalizeName(name).includes('burpee'),
    data: {
      name: 'Burpee',
      primaryMuscles: ['full body'],
      instructions: [
        'Start standing tall, feet shoulder-width apart.',
        'Drop into a squat and place your hands on the floor just outside your feet.',
        'Jump or step your feet back to a plank, keeping your core braced.',
        'Do a push-up (optional), then jump or step feet back under you.',
        'Explosively jump straight up, reaching overhead, then land softly and repeat.',
      ],
      images: [],
    },
  },
];
const tokenize = (value = '') => {
  const core = new Set();
  const equipment = new Set();
  normalizeName(value)
    .split(' ')
    .map((token) => {
      const trimmed = token.trim();
      if (!trimmed) return '';
      let base = trimmed.replace(/s$/, '');
      if (base === 'tricep') base = 'triceps';
      return base;
    })
    .filter(Boolean)
    .forEach((token) => {
      if (ignoreTokens.has(token)) return;
      if (equipmentTokens.has(token)) {
        equipment.add(token);
      } else {
        core.add(token);
      }
    });
  return { core, equipment };
};
const scoreTokenOverlap = (target = new Set(), candidate = new Set()) => {
  if (!target.size || !candidate.size) return 0;
  let intersection = 0;
  target.forEach((token) => {
    if (candidate.has(token)) intersection += 1;
  });
  const unionSize = new Set([...target, ...candidate]).size || 1;
  const jaccard = intersection / unionSize;
  const coverage = intersection / Math.max(1, target.size);
  return (jaccard * 0.7) + (coverage * 0.3);
};
const getDayIndex = (dayName) => {
  const idx = days.indexOf(dayName);
  return idx === -1 ? 7 : idx;
};

const WORKOUT_PROGRESS_KEY = 'cl_workout_progress_v1';
const WORKOUT_WEIGHT_LOG_KEY = 'cl_workout_weight_log_v1';
const WEEK_SYNC_INTERVAL = 60 * 60 * 1000; // 1 hour

const createDefaultProgress = () => ({
  streakCount: 0,
  lastCompletionDate: null,
  weeks: {},
});

const getWeekStartDate = (dateLike) => {
  const date = new Date(dateLike);
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay();
  start.setDate(start.getDate() - day);
  return start;
};

const formatDateKey = (dateLike) => {
  const d = new Date(dateLike);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
};

const getWeekKey = (dateLike) => formatDateKey(getWeekStartDate(dateLike));

const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const loadProgressRecord = (userKey) => {
  if (typeof window === 'undefined') return createDefaultProgress();
  const raw = window.localStorage.getItem(WORKOUT_PROGRESS_KEY);
  if (!raw) return createDefaultProgress();
  const parsed = safeParseJson(raw) || {};
  const stored = parsed[userKey];
  if (!stored) return createDefaultProgress();
  return {
    streakCount: stored.streakCount || 0,
    lastCompletionDate: stored.lastCompletionDate || null,
    weeks: stored.weeks || {},
  };
};

const saveProgressRecord = (userKey, record) => {
  if (typeof window === 'undefined') return;
  const raw = window.localStorage.getItem(WORKOUT_PROGRESS_KEY);
  const parsed = safeParseJson(raw) || {};
  parsed[userKey] = record;
  window.localStorage.setItem(WORKOUT_PROGRESS_KEY, JSON.stringify(parsed));
};

const computeStreakFromWeeks = (weeksMap = {}) => {
  const todayKey = formatDateKey(new Date());
  const completedDates = new Set();

  Object.entries(weeksMap).forEach(([weekKey, dayMap]) => {
    if (!dayMap) return;
    const weekStart = new Date(weekKey);
    days.forEach((dayName, idx) => {
      if (dayMap[dayName]) {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + idx);
        completedDates.add(formatDateKey(d));
      }
    });
  });

  let streak = 0;
  let cursor = new Date(todayKey);
  while (completedDates.has(formatDateKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  const lastCompletionDate = completedDates.size
    ? Array.from(completedDates).sort().pop()
    : null;

  return { streak, lastCompletionDate };
};

const loadWeightLog = (userKey, weekKey) => {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(WORKOUT_WEIGHT_LOG_KEY);
  const parsed = safeParseJson(raw) || {};
  return parsed?.[userKey]?.[weekKey] || {};
};

const saveWeightLog = (userKey, weekKey, weekLog) => {
  if (typeof window === 'undefined') return;
  const raw = window.localStorage.getItem(WORKOUT_WEIGHT_LOG_KEY);
  const parsed = safeParseJson(raw) || {};
  const userData = parsed[userKey] || {};
  userData[weekKey] = weekLog;
  parsed[userKey] = userData;
  window.localStorage.setItem(WORKOUT_WEIGHT_LOG_KEY, JSON.stringify(parsed));
};

function WorkoutDayTracker({ completedDays, weekStartDate, streakCount }) {
  const currentDate = useMemo(() => new Date(), []);
  const todayIndex = currentDate.getDay();
  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    const mod = day % 10;
    return mod === 1 ? 'st' : mod === 2 ? 'nd' : mod === 3 ? 'rd' : 'th';
  };
  // Compute week start (Sunday) and format: Week of the 2nd of November
  const weekStart = useMemo(() => {
    if (weekStartDate) return new Date(weekStartDate);
    const d = new Date(currentDate);
    const diff = d.getDay(); // 0 is Sunday
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [weekStartDate, currentDate]);
  const weekOfTitle = useMemo(() => {
    const day = weekStart.getDate();
    const month = weekStart.toLocaleDateString('en-US', { month: 'long' });
    return `Week of the ${day}${getOrdinalSuffix(day)} of ${month}`;
  }, [weekStart]);

  return (
    <div className="day-tracker compact">
      <div className="date-info"><h3>{weekOfTitle}</h3></div>
      <div className="day-circles">
        {daysOfWeek.map((d, i) => (
          <div key={i} className={`circle ${i === todayIndex ? 'today' : ''} ${completedDays[i] ? 'filled' : ''}`}>
            <span className={completedDays[i] ? 'day-text-filled' : 'day-text'}>{d}</span>
          </div>
        ))}
      </div>
      <div><p className="streak-info"><span>🔥 Streak</span>{streakCount} day{streakCount !== 1 ? 's' : ''}</p></div>
    </div>
  );
}

const LOADING_STEPS = [
  'Reviewing your goals and availability...',
  'Mapping the best split for strength and recovery...',
  'Sequencing focused sessions with built-in rest...',
  'Dialing training days to your preferred schedule...',
  'Balancing push/pull/legs volume for your goal...',
  'Picking low-impact cardio and safe substitutions...',
  'Guardrailing total sets for your experience level...',
  'Double-checking rest day spacing so you recover...',
  'Writing finishers and core to round things out...',
];

const EmptyPlanState = ({ onOpenPreferences, needsSetup, generating }) => (
  <div className="card meal-card empty-plan-card">
    <img className="empty-plan-illustration" src={workoutPlanIllustration} alt="Workout plan preview" />
    <div className="empty-plan-card-content">
      <h4 className="empty-plan-card-title">Ready to get your personalized workout plan?</h4>
      <p className="muted empty-plan-card-copy">
        {needsSetup
          ? 'Set your weekly training days and we will build a balanced split for you.'
          : 'Tap below to get started'}
      </p>
      <p className="muted empty-plan-card-copy">
        Your coach will review your personalized plan within the next 24hrs to ensure plan accuracy in alignment with your goals.
      </p>
      {generating && (
        <div className="empty-plan-loader">
          <LoadingAnimation />
        </div>
      )}
      <button className="btn-primary empty-plan-cta" onClick={onOpenPreferences} disabled={generating}>
        {generating ? 'Generating...' : 'Generate Workout Plan'}
      </button>
    </div>
  </div>
);

const WorkoutLoadingOverlay = ({ message }) => (
  <div className="workout-loading-overlay">
    <div className="loading-animation-wrapper fullscreen with-message">
      <div className="loader" />
      <p className="plan-loading-message">
        {message || 'Building your personalized training week...'}
      </p>
    </div>
  </div>
);

export default function WorkoutPage() {
  const [expandedIdx, setExpandedIdx] = useState(-1);
  const [data, setData] = useState([]);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTab, setInfoTab] = useState('overview');
  const [openExercise, setOpenExercise] = useState(null);
  const [exerciseDbList, setExerciseDbList] = useState([]);
  const [trainingDays, setTrainingDays] = useState(null);
  const [workoutPreferences, setWorkoutPreferences] = useState(null);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [completedMap, setCompletedMap] = useState({});
  const [streakCount, setStreakCount] = useState(0);
  const [activeWeekKey, setActiveWeekKey] = useState(() => getWeekKey(new Date()));
  const [loadingMessage, setLoadingMessage] = useState('');
  const [setLog, setSetLog] = useState({});
  const [previousSetLog, setPreviousSetLog] = useState({});
  const [trackingOpenMap, setTrackingOpenMap] = useState({});
  const [progressModal, setProgressModal] = useState(null);
  const progressUserKey = userId || 'anonymous';
  const weekStartDate = useMemo(() => {
    const parsed = new Date(activeWeekKey);
    return Number.isNaN(parsed.getTime()) ? getWeekStartDate(new Date()) : parsed;
  }, [activeWeekKey]);
  const openTrainingModal = useCallback(() => setShowTrainingModal(true), []);
  const loaderTimerRef = useRef(null);

  const exerciseLibrary = useMemo(() => {
    const map = new Map();
    (exercisesData || []).forEach((ex) => {
      if (!ex?.name) return;
      const key = normalizeName(ex.name);
      if (!key) return;
      map.set(key, { ...ex, source: 'json' });
    });
    (exerciseDbList || []).forEach((ex) => {
      if (!ex?.name) return;
      const key = normalizeName(ex.name);
      if (!key) return;
      map.set(key, { ...ex, source: 'db' });
    });
    return Array.from(map.values());
  }, [exerciseDbList]);

  // Build a quick lookup by normalized name plus token sets for fuzzy matching
  const exerciseLookup = useMemo(() => (
    (exerciseLibrary || []).map((ex) => ({
      meta: ex,
      normalized: normalizeName(ex.name),
      tokens: tokenize(ex.name),
    }))
  ), [exerciseLibrary]);
  const exerciseIndex = useMemo(() => {
    const idx = new Map();
    exerciseLookup.forEach(({ normalized, meta }) => {
      idx.set(normalized, meta);
    });
    return idx;
  }, [exerciseLookup]);

  const extractSteps = (text = '') => {
    const steps = [];
    if (!text) return steps;
    const numbered = /\d+\.\s*/g.test(text);
    if (numbered) {
      const regex = /(\d+)\.\s*([^]+?)(?=(\d+\.\s)|$)/g;
      let match;
      while ((match = regex.exec(text)) !== null) {
        const step = match[2].trim();
        if (step) steps.push(step);
      }
    }
    if (!steps.length) {
      text.split(/\n+/).forEach((line) => {
        const step = line.replace(/^\d+\.\s*/, '').trim();
        if (step) steps.push(step);
      });
    }
    return steps;
  };

  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('../supabaseClient');
        const { data: auth } = await supabase.auth.getUser();
        setUserId(auth?.user?.id || null);
      } catch {
        setUserId(null);
      }
    })();
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { supabase } = await import('../supabaseClient');
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user) return;
        const { data, error } = await supabase
          .from('exercise_list')
          .select('id, name, force, level, mechanic, equipment, primary_muscles, secondary_muscles, instructions, category, images');
        if (error) throw error;
        if (!active) return;
        const mapped = (data || []).map((row) => ({
          id: row.id,
          name: row.name,
          force: row.force,
          level: row.level,
          mechanic: row.mechanic,
          equipment: row.equipment,
          primaryMuscles: Array.isArray(row.primary_muscles) ? row.primary_muscles : [],
          secondaryMuscles: Array.isArray(row.secondary_muscles) ? row.secondary_muscles : [],
          instructions: Array.isArray(row.instructions) ? row.instructions : [],
          category: row.category,
          images: Array.isArray(row.images) ? row.images : [],
          source: 'db',
        }));
        setExerciseDbList(mapped);
      } catch (_) {
      }
    })();
    return () => {
      active = false;
    };
  }, []);


  const findExercise = useCallback((item) => {
    const name = typeof item === 'string' ? item : item?.name;
    if (!name) return null;
    const key = normalizeName(name);
    let result = null;
    if (exerciseIndex.has(key)) {
      result = exerciseIndex.get(key);
    } else {
      for (const [k, v] of exerciseIndex.entries()) {
        if (k.includes(key) || key.includes(k)) {
          result = v;
          break;
        }
      }
      if (!result) {
        const targetTokens = tokenize(name);
        let best = null;
        let bestScore = 0;
        exerciseLookup.forEach(({ meta, tokens }) => {
          const baseScore = scoreTokenOverlap(targetTokens.core, tokens.core);
          const equipmentOverlap = scoreTokenOverlap(targetTokens.equipment, tokens.equipment);
          // Bonus for matching equipment; small penalty when equipment conflicts.
          const hasEquipmentRequest = targetTokens.equipment.size > 0;
          const equipmentPenalty = hasEquipmentRequest && tokens.equipment.size > 0 && equipmentOverlap === 0 ? 0.08 : 0;
          let coreIntersection = 0;
          targetTokens.core.forEach((token) => {
            if (tokens.core.has(token)) coreIntersection += 1;
          });
          const extraCorePenalty = Math.max(0, tokens.core.size - coreIntersection) * 0.04;
          const score = baseScore + (equipmentOverlap * 0.12) - equipmentPenalty - extraCorePenalty;
          if (score > bestScore) {
            best = meta;
            bestScore = score;
          }
        });
        if (best && bestScore >= 0.45) {
          result = best;
        }
      }
    }

    const fallbackDescription = (typeof item === 'object' && item?.description) ? item.description : '';
    const fallbackSteps = extractSteps(fallbackDescription);

    if (result) {
      const instructions = result.instructions?.length ? result.instructions : fallbackSteps;
      return { ...result, name, instructions };
    }

    const manual = manualExerciseFallbacks.find((m) => m.match(name));
    return {
      name,
      ...(manual ? manual.data : {}),
      instructions: fallbackSteps.length
        ? fallbackSteps
        : (manual?.data?.instructions || ['Maintain control throughout the movement.']),
      images: manual?.data?.images || [],
      primaryMuscles: manual?.data?.primaryMuscles || [],
    };
  }, [exerciseIndex, exerciseLookup]);

  const handleOpenExercise = useCallback(async (item) => {
    const exercise = findExercise(item);
    if (!exercise) return;
    const images = Array.isArray(exercise.images) ? exercise.images : [];
    if (exercise.source !== 'db' || images.length === 0) {
      setOpenExercise(exercise);
      return;
    }
    try {
      const { supabase } = await import('../supabaseClient');
      const resolved = await Promise.all(images.map(async (path) => {
        if (!path) return null;
        if (path.startsWith('http') || path.startsWith('data:')) return path;
        const { data, error } = await supabase.storage
          .from(EXERCISE_IMAGE_BUCKET)
          .createSignedUrl(path, EXERCISE_IMAGE_URL_TTL);
        if (error) return null;
        return data?.signedUrl || null;
      }));
      setOpenExercise({ ...exercise, images: resolved.filter(Boolean) });
    } catch (_) {
      setOpenExercise(exercise);
    }
  }, [findExercise]);

  const getMuscleSummary = useCallback((workoutDay) => {
    if (!workoutDay) return '';
    if (workoutDay.displayFocus) return workoutDay.displayFocus;
    const type = (workoutDay.type || '').toLowerCase();
    if (type === 'cardio') return 'Cardio';
    if (type === 'recovery') return 'Recovery / Mobility';
    const counts = new Map();
    (workoutDay.items || []).forEach((item) => {
      const meta = findExercise(item);
      (meta?.primaryMuscles || []).forEach((m) => {
        const muscle = (m || '').trim();
        if (!muscle) return;
        counts.set(muscle, (counts.get(muscle) || 0) + 1);
      });
    });
    const ranked = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([muscle]) => muscle);
    return ranked.slice(0, 3).join(', ') || workoutDay.muscles || workoutDay.focus || '';
  }, [findExercise]);

  // Image resolver for ExerciseModal
  const imgCtx = useMemo(() => {
    try { return require.context('../assets/exercises', true); } catch { return null; }
  }, []);
  const getImage = (relativePath) => {
    if (!relativePath) return '';
    if (relativePath.startsWith('http') || relativePath.startsWith('data:')) return relativePath;
    if (!imgCtx) return '';
    try { return imgCtx('./' + relativePath); } catch { return ''; }
  };

  const isTimedExercise = (item = {}) => {
    const repsText = String(item.reps || '').toLowerCase();
    const timeText = String(item.time || '').toLowerCase();
    const nameText = String(item.name || '').toLowerCase();
    const hasExplicitTime = /(\d+\s*(s|sec|second|seconds|min|minute|minutes|m))/.test(repsText) || /(\d+\s*(s|sec|second|seconds|min|minute|minutes|m))/.test(timeText);
    const hasTimeKeyword = repsText.includes('time') || timeText.includes('time');
    const nameKeywords = ['plank', 'hold', 'treadmill', 'bike', 'elliptical', 'rower', 'rowing', 'run', 'jog', 'walk', 'airdyne', 'assault'];
    const hasNameKeyword = nameKeywords.some((k) => nameText.includes(k));
    return hasExplicitTime || hasTimeKeyword || hasNameKeyword;
  };

  // Load training days preference
  useEffect(() => {
    const localValue = Number(localStorage.getItem('training_days')) || null;
    if (localValue && localValue >= 2 && localValue <= 5) {
      setTrainingDays(localValue);
    }
    (async () => {
      try {
        const { data } = await getMyWorkoutPreferences();
        if (data) {
          setWorkoutPreferences(data);
          const remoteDays = Number(data.training_days);
          if (remoteDays && remoteDays >= 2 && remoteDays <= 5) {
            setTrainingDays(remoteDays);
            localStorage.setItem('training_days', String(remoteDays));
          } else if (!(localValue && localValue >= 2 && localValue <= 5)) {
            setShowTrainingModal(true);
          }
        } else if (!(localValue && localValue >= 2 && localValue <= 5)) {
          setShowTrainingModal(true);
        }
      } catch {
        if (!(localValue && localValue >= 2 && localValue <= 5)) {
          setShowTrainingModal(true);
        }
      }
    })();
  }, []);

  async function saveTrainingDays({
    days,
    primaryGoal = '',
    limitations,
    focusAreas = [],
    experienceLevel = null,
    splitPreference = null,
    preferredDays = [],
  }) {
    try {
      localStorage.setItem('training_days', String(days));
      setTrainingDays(days);
      setShowTrainingModal(false);
      await upsertWorkoutPreferences({
        training_days: days,
        primary_goal: primaryGoal || null,
        limitations: Array.isArray(limitations) && limitations.length ? limitations : ['None'],
        focus_muscle_groups: focusAreas || [],
        experience_level: experienceLevel || null,
        preferred_split: splitPreference || null,
        preferred_training_days: preferredDays || [],
      });
      setWorkoutPreferences({
        training_days: days,
        primary_goal: primaryGoal || null,
        limitations: Array.isArray(limitations) && limitations.length ? limitations : ['None'],
        focus_muscle_groups: focusAreas || [],
        experience_level: experienceLevel || null,
        preferred_split: splitPreference || null,
        preferred_training_days: preferredDays || [],
      });
      // Generate a plan after saving preferences
      setGenerating(true);
      const { supabase } = await import('../supabaseClient');
      const { data: sessionData } = await supabase.auth.getSession();
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      const accessToken = sessionData?.session?.access_token;
      if (userId) {
        const genRes = await apiFetch('/generate-workout-plan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, accessToken, trainingDays: days, detailLevel: 'lite' })
        });
        try {
          const genJson = await genRes.json();
          // Log the raw generated plan and UI mapping for debugging
          // If the API returned a UI mapping, apply it immediately
          if (genJson?.ui && Array.isArray(genJson.ui) && genJson.ui.length) {
            setData(genJson.ui);
          }
        } catch { }
        await getPlan();
      }
    } catch (_) { }
    finally { setGenerating(false); }
  }

  async function getPlan() {
    setIsPlanLoading(true);
    try {
      const { supabase } = await import('../supabaseClient');
      const { data: sessionData } = await supabase.auth.getSession();
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) return;
      const res = await apiFetch(`/workout-plan?userId=${encodeURIComponent(userId)}&accessToken=${encodeURIComponent(sessionData?.session?.access_token || '')}`);
      const json = await res.json();
      if (Array.isArray(json?.ui)) {
        let nextUi = json.ui;
        if (nextUi.length && json?.programId) {
          try {
            const { data: dayRows } = await supabase
              .from('workout_days')
              .select('day_of_week, display_name, display_focus, display_duration_min, display_type, raw_payload')
              .eq('program_id', json.programId);
            if (Array.isArray(dayRows) && dayRows.length) {
              const byDay = new Map(dayRows.map((row) => [row.day_of_week, row]));
              nextUi = nextUi.map((day) => {
                const idx = getDayIndex(day.day);
                const row = byDay.get(idx);
                if (!row) return day;
                const updated = { ...day };
                if (row.display_name) updated.focus = row.display_name;
                if (row.display_focus) {
                  updated.displayFocus = row.display_focus;
                  updated.muscles = row.display_focus;
                }
                if (row.display_duration_min != null) updated.duration = `${row.display_duration_min} min`;
                if (row.display_type) updated.type = row.display_type;
                const payload = row.raw_payload && typeof row.raw_payload === 'object' ? row.raw_payload : {};
                const notes = typeof payload.notes === 'string' ? payload.notes.trim() : '';
                updated.notes = notes;
                return updated;
              });
            }
            if (process.env.NODE_ENV !== 'production') {
            }
          } catch (_) {
            // If the display fields aren't available, keep the base UI.
          }
        }
        setData(nextUi);
      } else {
        setData([]);
      }
    } catch {
      setData([]);
    } finally {
      setIsPlanLoading(false);
    }
  }

  // Try to load an existing plan when trainingDays is present
  useEffect(() => {
    if (trainingDays) {
      getPlan();
    }
  }, [trainingDays]);

  useEffect(() => {
    if (!infoOpen) {
      setInfoTab('overview');
    }
  }, [infoOpen]);

  const hydratedData = useMemo(() => {
    const ordered = [...data].sort((a, b) => getDayIndex(a.day) - getDayIndex(b.day));
    return ordered.map((w) => ({
      ...w,
      completed: !!completedMap[w.day],
    }));
  }, [data, completedMap]);

  const hasPlanData = hydratedData.length > 0;

  const generatingFirstTime = generating && !hasPlanData;
  const showInitialLoader = isPlanLoading && !hasPlanData && !generatingFirstTime;
  const showLoadingMessages = generatingFirstTime;
  const showPlanInfoButton = hasPlanData && !generating;
  useEffect(() => {
    if (showLoadingMessages && LOADING_STEPS.length) {
      setLoadingMessage(LOADING_STEPS[0]);
      let index = 1;
      loaderTimerRef.current = setInterval(() => {
        setLoadingMessage((prev) => {
          const next = LOADING_STEPS[index % LOADING_STEPS.length];
          index += 1;
          return next;
        });
      }, 2200);
    } else {
      if (loaderTimerRef.current) {
        clearInterval(loaderTimerRef.current);
        loaderTimerRef.current = null;
      }
      setLoadingMessage('');
    }
    return () => {
      if (loaderTimerRef.current) {
        clearInterval(loaderTimerRef.current);
        loaderTimerRef.current = null;
      }
    };
  }, [showLoadingMessages]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncWeekKey = () => {
      const current = getWeekKey(new Date());
      setActiveWeekKey((prev) => (prev === current ? prev : current));
    };
    syncWeekKey();
    const timer = window.setInterval(syncWeekKey, WEEK_SYNC_INTERVAL);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = loadProgressRecord(progressUserKey);
    setStreakCount(stored.streakCount || 0);
    setCompletedMap(stored.weeks?.[activeWeekKey] || {});
  }, [progressUserKey, activeWeekKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setSetLog(loadWeightLog(progressUserKey, activeWeekKey));
  }, [progressUserKey, activeWeekKey]);

  const buildLogKey = useCallback((dayName, exerciseName) => `${normalizeName(dayName)}::${normalizeName(exerciseName)}`, []);

  useEffect(() => {
    if (!userId || !activeWeekKey) return;
    (async () => {
      try {
        const { supabase } = await import('../supabaseClient');
        const { data } = await supabase
          .from('workout_set_logs')
          .select('week_key, day_name, exercise_name, set_index, weight, reps, duration_seconds, done, updated_at')
          .eq('user_id', userId)
          .order('week_key', { ascending: false })
          .order('updated_at', { ascending: false });

        const localWeek = loadWeightLog(progressUserKey, activeWeekKey);
        const currentWeekLog = { ...localWeek };
        const historyLog = {};

        (data || []).forEach((row) => {
          const key = buildLogKey(row.day_name, row.exercise_name);
          if (row.week_key === activeWeekKey) {
            const arr = Array.isArray(currentWeekLog[key]?.entries) ? [...currentWeekLog[key].entries] : [];
            arr[row.set_index] = {
              weight: row.weight != null ? String(row.weight) : '',
              reps: row.reps != null ? String(row.reps) : '',
              duration: row.duration_seconds != null ? String(row.duration_seconds) : '',
              done: !!row.done,
            };
            currentWeekLog[key] = { entries: arr };
          } else {
            if (!historyLog[key]) {
              historyLog[key] = { entries: [], week: row.week_key };
            }
            const arr = Array.isArray(historyLog[key].entries) ? [...historyLog[key].entries] : [];
            if (!arr[row.set_index]) {
              arr[row.set_index] = {
                weight: row.weight != null ? String(row.weight) : '',
                reps: row.reps != null ? String(row.reps) : '',
                duration: row.duration_seconds != null ? String(row.duration_seconds) : '',
                done: !!row.done,
              };
            }
            historyLog[key] = { entries: arr, week: historyLog[key].week };
          }
        });

        setSetLog(currentWeekLog);
        setPreviousSetLog(historyLog);
        saveWeightLog(progressUserKey, activeWeekKey, currentWeekLog);
      } catch (err) {
        console.error('Failed to load remote set logs', err);
      }
    })();
  }, [activeWeekKey, buildLogKey, progressUserKey, userId]);

  const persistSetLogRemote = useCallback(async (dayName, exerciseName, setIndex, entry, shouldDelete = false) => {
    if (!userId) return;
    try {
      const { supabase } = await import('../supabaseClient');
      if (shouldDelete) {
        await supabase.from('workout_set_logs')
          .delete()
          .match({
            user_id: userId,
            week_key: activeWeekKey,
            day_name: dayName,
            exercise_name: exerciseName,
            set_index: setIndex,
          });
        return;
      }
      const toNumber = (val) => {
        if (val === '' || val === null || val === undefined) return null;
        const num = Number(val);
        return Number.isFinite(num) ? num : null;
      };
      const payload = {
        user_id: userId,
        week_key: activeWeekKey,
        day_name: dayName,
        exercise_name: exerciseName,
        set_index: setIndex,
        weight: toNumber(entry.weight),
        reps: toNumber(entry.reps),
        duration_seconds: toNumber(entry.duration),
        done: !!entry.done,
      };
      const { error } = await supabase.from('workout_set_logs')
        .upsert([payload], { onConflict: 'user_id,week_key,day_name,exercise_name,set_index', ignoreDuplicates: false });
      if (error && (error.code === '23505' || error.status === 409)) {
        await supabase.from('workout_set_logs')
          .update(payload)
          .match({
            user_id: userId,
            week_key: activeWeekKey,
            day_name: dayName,
            exercise_name: exerciseName,
            set_index: setIndex,
          });
      } else if (error) {
        throw error;
      }
    } catch (err) {
      console.error('Failed to sync workout set', err);
    }
  }, [activeWeekKey, userId]);

  const updateSetLog = useCallback((dayName, exerciseName, setIndex, partial) => {
    setSetLog((prev) => {
      const next = { ...prev };
      const key = buildLogKey(dayName, exerciseName);
      const currentEntries = Array.isArray(next[key]?.entries) ? [...next[key].entries] : [];
      const existing = currentEntries[setIndex] || { weight: '', reps: '', duration: '', done: false };
      const updatedEntry = { ...existing, ...partial };

      const shouldDelete = !(updatedEntry.weight || updatedEntry.reps || updatedEntry.duration || updatedEntry.done);
      if (shouldDelete) {
        currentEntries[setIndex] = { weight: '', reps: '', duration: '', done: false };
      } else {
        currentEntries[setIndex] = updatedEntry;
      }

      // Trim empty trailing entries to keep storage small
      while (currentEntries.length && !(currentEntries[currentEntries.length - 1]?.weight || currentEntries[currentEntries.length - 1]?.reps || currentEntries[currentEntries.length - 1]?.done)) {
        currentEntries.pop();
      }

      if (currentEntries.length) {
        next[key] = { entries: currentEntries };
      } else {
        delete next[key];
      }

      saveWeightLog(progressUserKey, activeWeekKey, next);
      persistSetLogRemote(dayName, exerciseName, setIndex, updatedEntry, shouldDelete);
      return next;
    });
  }, [activeWeekKey, buildLogKey, persistSetLogRemote, progressUserKey]);

  const getSetEntries = useCallback((dayName, exerciseName) => {
    const key = buildLogKey(dayName, exerciseName);
    const entries = setLog?.[key]?.entries;
    return Array.isArray(entries) ? entries : [];
  }, [buildLogKey, setLog]);

  const getPreviousSetData = useCallback((dayName, exerciseName) => {
    const key = buildLogKey(dayName, exerciseName);
    return previousSetLog?.[key] || { entries: [], week: null };
  }, [buildLogKey, previousSetLog]);

  const persistProgress = useCallback((updatedWeekMap, targetWeekKey) => {
    if (typeof window === 'undefined') return;
    const currentWeekKey = targetWeekKey || getWeekKey(new Date());
    if (currentWeekKey !== activeWeekKey) {
      setActiveWeekKey(currentWeekKey);
    }
    const stored = loadProgressRecord(progressUserKey);
    const weeks = { ...(stored.weeks || {}), [currentWeekKey]: updatedWeekMap };
    const { streak, lastCompletionDate } = computeStreakFromWeeks(weeks);
    const record = { streakCount: streak, lastCompletionDate, weeks };
    saveProgressRecord(progressUserKey, record);
    setStreakCount(streak);
  }, [progressUserKey, activeWeekKey]);

  const completedDays = useMemo(() => {
    return days.map((dayName) => !!completedMap[dayName]);
  }, [completedMap]);

  const focusDisplay = useMemo(() => {
    const list = Array.isArray(workoutPreferences?.focus_muscle_groups)
      ? workoutPreferences.focus_muscle_groups.filter(Boolean)
      : [];
    if (!list.length || list.includes('ALL')) return ['All muscle groups'];
    return list;
  }, [workoutPreferences]);

  const preferredDayDisplay = useMemo(() => {
    const list = Array.isArray(workoutPreferences?.preferred_training_days)
      ? workoutPreferences.preferred_training_days.filter(Boolean)
      : [];
    return list;
  }, [workoutPreferences]);

  const limitationsDisplay = useMemo(() => {
    const list = Array.isArray(workoutPreferences?.limitations)
      ? workoutPreferences.limitations.filter((item) => item && item !== 'None')
      : [];
    return list.length ? list : ['None'];
  }, [workoutPreferences]);

  const splitDisplay = useMemo(() => {
    if (workoutPreferences?.preferred_split?.title) return workoutPreferences.preferred_split.title;
    if (workoutPreferences?.preferred_split?.code) return workoutPreferences.preferred_split.code;
    return 'Balanced full-body';
  }, [workoutPreferences]);

  const experienceDisplay = useMemo(() => {
    const value = workoutPreferences?.experience_level;
    if (!value) return 'Not set';
    const map = {
      complete_beginner: 'Complete beginner',
      basic_familiarity: 'Basic familiarity',
      intermediate: 'Intermediate trainer',
      advanced: 'Advanced lifter',
    };
    if (map[value]) return map[value];
    return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }, [workoutPreferences]);

  const primaryGoalDisplay = useMemo(() => {
    return workoutPreferences?.primary_goal || 'Not set';
  }, [workoutPreferences]);

  const markComplete = (idx) => {
    const target = data[idx];
    if (!target) return;
    const dayName = target.day;
    const currentWeekKey = getWeekKey(new Date());
    const newWeekStarted = currentWeekKey !== activeWeekKey;
    setCompletedMap((prev) => {
      const base = newWeekStarted ? {} : { ...prev };
      const updated = { ...base };
      if (updated[dayName]) {
        delete updated[dayName];
      } else {
        updated[dayName] = true;
      }
      persistProgress(updated, currentWeekKey);
      return updated;
    });
  };

  return (
    <>
      {showInitialLoader && <LoadingAnimation fullScreen />}
      <NavBar />
      <main className="web-view">
        {/* One-time training days selector */}
        <TrainingDaysModal
          open={showTrainingModal}
          initialDays={trainingDays || 5}
          initialGoal={workoutPreferences?.primary_goal || workoutPreferences?.goal_primary || ''}
          includeGoalStep={hasPlanData}
          onSave={saveTrainingDays}
          onClose={() => setShowTrainingModal(false)}
          canDismiss
        />

        {generatingFirstTime ? (
          <WorkoutLoadingOverlay message={loadingMessage} />
        ) : (
          <div className="container">
            <div className="card">
              <div className="card-header meal-header">
                <div>
                  <h3>Workout Plan</h3>
                  <p>Your weekly training schedule</p>
                </div>
                <div className="meal-header-actions">
                  {showPlanInfoButton && (
                    <button className="info-button" aria-label="Plan Info" onClick={() => setInfoOpen(true)}>
                      <InfoIcon className="icon" />
                    </button>
                  )}
                </div>
              </div>
              {isPlanLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                  <LoadingAnimation />
                </div>
              ) : hasPlanData ? (
                <WorkoutDayTracker completedDays={completedDays} weekStartDate={weekStartDate} streakCount={streakCount} />
              ) : (
                <div className="empty-tracker">
                  <p>
                    {trainingDays
                      ? "No workouts on deck yet. Once your plan has been created there are no excuses, just execution."
                      : 'Choose your training days below and start your fitness transformation. Action beats waiting.'}
                  </p>
                </div>
              )}
            </div>

            {isPlanLoading ? null : hasPlanData ? (
              hydratedData.map((w, idx) => {
                const Icon = w.type === 'cardio' ? CardioIcon : (w.type === 'recovery' ? RestIcon : DumbbellIcon);
                const expanded = expandedIdx === idx;
                const thumbClass = w.type === 'cardio' ? 'workout-thumb-blue' : (w.type === 'recovery' ? 'workout-thumb-green' : 'workout-thumb-red');
                const dayCompleted = !!w.completed;
                const cardClass = `card meal-card${dayCompleted ? ' completed' : ''}`;
                return (
                  <div key={w.day} className={cardClass}>
                    {!expanded ? (
                      <div className="meal-collapsed" onClick={() => setExpandedIdx(idx)} role="button" aria-expanded={expanded}>
                        <div className="meal-collapsed-text">
                          <div className="meal-title-row">
                            <div className="meal-title">{w.day}</div>
                            {dayCompleted && (
                              <span className="completion-pill">
                                <CheckIcon className="completion-icon" />
                                Completed
                              </span>
                            )}
                          </div>
                          <div className="meal-name">{w.focus}</div>
                          <div className="meal-kcal">{w.duration}</div>
                          {getMuscleSummary(w) && <div className="muted">{getMuscleSummary(w)}</div>}
                          <button className="see-more-btn" onClick={(e) => { e.stopPropagation(); setExpandedIdx(idx); }}>see more info</button>
                        </div>
                        <Icon className={thumbClass} />
                      </div>
                    ) : (
                      <div className="meal-expanded">
                        <div className="meal-expanded-header">
                          <div>
                            <div className="meal-title-row">
                              <div className="meal-title">{w.day}</div>
                              {dayCompleted && (
                                <span className="completion-pill">
                                  <CheckIcon className="completion-icon" />
                                  Completed
                                </span>
                              )}
                            </div>
                            <div className="meal-name">{w.focus}</div>
                            <div className="meal-kcal">{w.duration}</div>
                            {getMuscleSummary(w) && <div className="muted">{getMuscleSummary(w)}</div>}
                          </div>
                          <button className="workout-collapse-btn" aria-label="workout-collapse" onClick={() => setExpandedIdx(-1)}>
                            <CancelIcon className="cancel-icon" />
                          </button>
                        </div>

                        {w.notes && (
                          <div className="workout-day-notes">
                            <div className="workout-day-notes-label">Coach notes</div>
                            <div className="workout-day-notes-text">{w.notes}</div>
                          </div>
                        )}

                        <div className="list">
                          {w.items.map((it, i) => {
                            const setEntries = getSetEntries(w.day, it.name);
                            const prevSetData = getPreviousSetData(w.day, it.name);
                            const prevEntries = prevSetData.entries || [];
                            const isTimed = isTimedExercise(it);
                            const parsedSets = Number.parseInt(it.sets, 10);
                            const baseSets = Number.isFinite(parsedSets) && parsedSets > 0 ? parsedSets : (it.sets ? 3 : 1);
                            const totalSets = Math.max(baseSets, setEntries.length || 0, 1);
                            const repTarget = it.reps || '–';
                            const targetValue = isTimed ? (it.time || it.reps || 'time') : repTarget;
                            const targetLabel = isTimed ? 'time' : 'reps';
                            const trackingKey = buildLogKey(w.day, it.name);
                            const trackingOpen = !!trackingOpenMap[trackingKey];

                            return (
                              <div key={i} className="list-item workout-exercise-card">
                                <div className="exercise-card-header">
                                  <div className="item-stack">
                                    <div className="item-title">{it.name}</div>
                                    <div className="item-sub">{[it.sets ? `${it.sets} sets` : null, it.reps ? `${it.reps}` : null, it.rest ? `Rest: ${it.rest}` : null].filter(Boolean).join(' | ')}</div>
                                  </div>
                                  <button
                                    type="button"
                                    className="view-exercise-btn secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTrackingOpenMap((prev) => ({ ...prev, [trackingKey]: !trackingOpen }));
                                    }}
                                  >
                                    {trackingOpen ? 'Close tracking' : 'Start Tracking Exercise'}
                                  </button>
                                  <button
                                    type="button"
                                    className="view-exercise-btn"
                                    onClick={(e) => { e.stopPropagation(); handleOpenExercise(it); }}
                                  >
                                    Form & tips
                                  </button>
                                  {prevEntries.length > 0 && (
                                    <button
                                      type="button"
                                      className="view-exercise-btn secondary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setProgressModal({
                                          exercise: it.name,
                                          entries: prevEntries,
                                          week: prevSetData.week || 'Previous',
                                        });
                                      }}
                                    >
                                      Progress
                                    </button>
                                  )}
                                </div>

                                {trackingOpen && (
                                  <div className="workout-set-tracker">
                                    {Array.from({ length: totalSets }).map((_, setIdx) => {
                                      const entry = setEntries?.[setIdx] || {};
                                      const weightValue = entry.weight ?? '';
                                      const repsValue = entry.reps ?? '';
                                      const prevEntry = prevEntries?.[setIdx] || {};
                                      const hasPrev = !!(prevEntries && prevEntries.length);
                                      const weightPlaceholder = hasPrev && prevEntry.weight ? `Last: ${prevEntry.weight}` : '--';
                                      const repsPlaceholder = hasPrev && prevEntry.reps ? `Last: ${prevEntry.reps}` : '-- reps';
                                      const durationPlaceholder = hasPrev && prevEntry.duration ? `Last: ${prevEntry.duration} sec` : '-- sec';
                                      const done = !!entry.done;

                                      return (
                                        <div key={setIdx} className={`set-row${done ? ' completed' : ''}`}>
                                          <div className="set-badge">
                                            <span className="set-number">{setIdx + 1}</span>
                                            <span className="set-label">set</span>
                                          </div>

                                          <div className="set-target">
                                            <div className="set-target-value">{targetValue}</div>
                                            <div className="set-target-label">{targetLabel}</div>
                                          </div>

                                          {!isTimed && (
                                            <>
                                              <div className="set-input">
                                                <label className="sr-only" htmlFor={`weight-${w.day}-${i}-${setIdx}`}>Weight for set {setIdx + 1}</label>
                                                <div className="input-shell">
                                                  <input
                                                    id={`weight-${w.day}-${i}-${setIdx}`}
                                                    type="number"
                                                    inputMode="decimal"
                                                    placeholder={weightPlaceholder}
                                                    value={weightValue}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => updateSetLog(w.day, it.name, setIdx, { weight: e.target.value })}
                                                  />
                                                  <span className="input-unit">lb</span>
                                                </div>
                                              </div>

                                              <div className="set-input reps-input">
                                                <label className="sr-only" htmlFor={`reps-${w.day}-${i}-${setIdx}`}>Reps completed set {setIdx + 1}</label>
                                                <input
                                                  id={`reps-${w.day}-${i}-${setIdx}`}
                                                  type="number"
                                                  inputMode="numeric"
                                                  placeholder={repsPlaceholder}
                                                  value={repsValue}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onChange={(e) => updateSetLog(w.day, it.name, setIdx, { reps: e.target.value })}
                                                />
                                              </div>
                                            </>
                                          )}

                                          {isTimed && (
                                            <div className="set-input reps-input">
                                              <label className="sr-only" htmlFor={`duration-${w.day}-${i}-${setIdx}`}>Duration for set {setIdx + 1} (seconds)</label>
                                              <input
                                                id={`duration-${w.day}-${i}-${setIdx}`}
                                                type="number"
                                                inputMode="numeric"
                                                placeholder={durationPlaceholder}
                                                value={entry.duration ?? ''}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => updateSetLog(w.day, it.name, setIdx, { duration: e.target.value })}
                                              />
                                            </div>
                                          )}

                                          <button
                                            type="button"
                                            className={`set-complete ${done ? 'checked' : ''}`}
                                            aria-pressed={done}
                                            onClick={(e) => { e.stopPropagation(); updateSetLog(w.day, it.name, setIdx, { done: !done }); }}
                                            title={done ? 'Mark set as not done' : 'Mark set as done'}
                                          >
                                            <CheckIcon className="set-complete-icon" />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
                          <button
                            className="mark-complete-btn"
                            aria-pressed={dayCompleted}
                            onClick={() => markComplete(idx)}
                            title={dayCompleted ? 'Unmark this workout day' : 'Mark this workout day complete'}
                          >
                            {dayCompleted ? 'Completed' : 'Mark complete'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <EmptyPlanState
                onOpenPreferences={openTrainingModal}
                needsSetup={!trainingDays}
                generating={generating || isPlanLoading}
              />
            )}
          </div>
        )}
      </main>

      {/* Info Modal with structure and rules */}
      {infoOpen && (
        <div className="modal-overlay" onClick={() => setInfoOpen(false)}>
          <div className="modal-content meal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="meal-modal-header">
              <h3>Workout Plan Info</h3>
              <CancelIcon className="cancel-icon" onClick={() => setInfoOpen(false)} />
            </div>

            <div className="meal-modal-tabs">
              <button
                className={`meal-modal-tab ${infoTab === 'overview' ? 'active' : ''}`}
                onClick={() => setInfoTab('overview')}
                type="button"
              >
                Overview
              </button>
              <button
                className={`meal-modal-tab ${infoTab === 'preferences' ? 'active' : ''}`}
                onClick={() => setInfoTab('preferences')}
                type="button"
              >
                My Preferences
              </button>
            </div>

            {infoTab === 'overview' ? (
              <>
                <div className="meal-modal-block">
                  <h4>How this plan works</h4>
                  <ul className="meal-guidelines">
                    <li>{trainingDays ? `Your plan is set to ${trainingDays} training days each week.` : 'Select your weekly availability so we can build the plan around it.'}</li>
                    <li>Each workout blends a main lift, supporting accessories, and a short finisher so you know exactly what to do when you hit the gym.</li>
                    <li>Expect 60-75 minute sessions including your warm up and cool down.</li>
                  </ul>
                </div>

                <div className="meal-modal-block">
                  <h4>Before you train</h4>
                  <ul className="meal-guidelines">
                    <li>Start with 5-10 minutes of ramp warm ups that target the muscles in today's focus.</li>
                    <li>Scan the workout card, note the rep targets, and pick a load that lets you finish with 1-2 quality reps in reserve.</li>
                    <li>Set up equipment and log weights in your phone or notebook so you can track progress.</li>
                  </ul>
                </div>

                <div className="meal-modal-block">
                  <h4>During each exercise</h4>
                  <ul className="meal-guidelines">
                    <li>Control the lowering phase, drive explosively on the way up, and own the pauses.</li>
                    <li>Rest 60-90 seconds on accessories and up to 2 minutes on primary lifts.</li>
                    <li>If you hit the top of the rep range easily, add a small weight jump next session.</li>
                  </ul>
                </div>

                <div className="meal-modal-block">
                  <h4>Recovery & progression</h4>
                  <ul className="meal-guidelines">
                    <li>Keep rest days between intense sessions so you never push more than 3 days in a row.</li>
                    <li>Light walks, mobility, hydration, and 7-8 hours of sleep accelerate recovery.</li>
                    <li>Tap "Mark complete" after each session to keep your streak and give better plan feedback.</li>
                  </ul>
                </div>

                {/* <div className="meal-modal-block">
                  <h4>Adjust your schedule</h4>
                  <p>If life changes, update your training days and we'll rebuild the plan around the new cadence.</p>
                  <button className="btn-ghost workout-adjust-btn" onClick={() => { setInfoOpen(false); openTrainingModal(); }}>
                    Adjust training days
                  </button>
                </div> */}
              </>
            ) : (
              <div className="meal-modal-block">
                <h4>My Preferences</h4>
                <div className="meal-preferences-summary">
                  <div className="preference-row">
                    <span className="preference-label">Training days</span>
                    <span className="preference-value">{trainingDays ? `${trainingDays} days/week` : 'Not set'}</span>
                  </div>
                  <div className="preference-row">
                    <span className="preference-label">Primary goal</span>
                    <span className="preference-value">{primaryGoalDisplay}</span>
                  </div>
                  <div className="preference-row">
                    <span className="preference-label">Preferred split</span>
                    <span className="preference-value">{splitDisplay}</span>
                  </div>
                  <div className="preference-row">
                    <span className="preference-label">Focus areas</span>
                    <span className="preference-value">{focusDisplay.join(', ')}</span>
                  </div>
                  <div className="preference-row">
                    <span className="preference-label">Preferred weekdays</span>
                    <span className="preference-value">
                      {preferredDayDisplay.length ? preferredDayDisplay.join(', ') : 'Not selected'}
                    </span>
                  </div>
                  <div className="preference-row">
                    <span className="preference-label">Experience level</span>
                    <span className="preference-value">{experienceDisplay}</span>
                  </div>
                  <div className="preference-row">
                    <span className="preference-label">Limitations</span>
                    <span className="preference-value">{limitationsDisplay.join(', ')}</span>
                  </div>
                </div>
                <p className="meal-preferences-hint" style={{ marginTop: 12 }}>
                  Need a different split or weekday lineup? Save your changes and regenerate to get an updated plan.
                </p>
                <button className="btn-ghost workout-adjust-btn" onClick={() => { setInfoOpen(false); openTrainingModal(); }}>
                  Edit preferences
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ExerciseModal isOpen={!!openExercise} onClose={() => setOpenExercise(null)} exercise={openExercise} getImage={getImage} />
      {progressModal && (
        <div className="modal-overlay" onClick={() => setProgressModal(null)}>
          <div className="modal-content exercise-modal progress-modal" onClick={(e) => e.stopPropagation()}>
            <div className="goal-accordion-container">
              <h3>{progressModal.exercise}</h3>
              <CancelIcon onClick={() => setProgressModal(null)} className="cancel-icon" />
            </div>
            <p className="muted" style={{ marginBottom: 10 }}>
              Last logged: {progressModal.week || 'Previous week'}
            </p>
            <div className="progress-entries">
              {(progressModal.entries || []).map((entry, idx) => (
                <div key={idx} className="progress-entry-row">
                  <span className="progress-entry-label">Set {idx + 1}</span>
                  <span className="progress-entry-value">{entry.weight ?? '--'} lb</span>
                  <span className="progress-entry-value">{entry.reps ?? '--'} reps</span>
                  <span className="progress-entry-value">{entry.duration ? `${entry.duration} sec` : '-- sec'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
